import { Container } from '../../odie';

import { BasicButton } from './BasicButton';
import { numberClamp } from '../util/mathTools';
import { BasicText } from './BasicText';
import {
  RichTextHorizontalAlign,
  RichTextVerticalAlign,
} from '@play-co/timestep-core/ui';
import { ColorId, blockColorMap, colorIds } from '../../game/defs/block';

// types
//-----------------------------------------------------------------------------
export type ColorListener = (value: ColorId) => void;

export type ColorPickerOptions = {
  value: ColorId;
  width: number;
  height: number;
  colors?: ColorId[];
  increment?: number;
};

// constants
//-----------------------------------------------------------------------------
const arrowImage = 'assets/ui/match2/editor/buttons/button.arrow.png';

/*
    number picker
*/
export class ColorPicker extends Container {
  // private
  //-------------------------------------------------------------------------
  // events
  public onChanged?: ColorListener;
  // scene
  private _text!: BasicText;
  // state
  private _value!: number;
  private _colorIds: readonly ColorId[] = colorIds;

  // properties
  //-------------------------------------------------------------------------
  public get value(): ColorId {
    return this._colorIds[this._value];
  }

  public set value(value: ColorId) {
    // set field
    this._value = this._colorIds.indexOf(value);

    // update text
    this._updateText();
  }

  // init
  //-------------------------------------------------------------------------
  constructor(options: ColorPickerOptions) {
    super();
    this.width = options.width;
    this.height = options.height;
    const increment = options.increment || 1;
    this._colorIds = options.colors ?? colorIds;

    // spawn decrement
    this._spawnDecrement(options, increment);

    // spawn text
    this._spawnText();

    // spawn increment
    this._spawnIncrement(options, increment);

    // set initial value
    this.value = options.value;

    this.setBackgroundColor('rgba(255, 255, 255, 0.1)');
  }

  public static assets(): string[] {
    return [arrowImage];
  }

  // private: updaters
  //-------------------------------------------------------------------------
  private _updateText() {
    // update text with value
    this._text.setText(`${this.value.toString()}`);
    this._text.setColor(
      `#${blockColorMap[this.value].base.toString(16) ?? '000000'}`,
    );
  }

  // private: spawn
  //-------------------------------------------------------------------------
  private _spawnDecrement(options: ColorPickerOptions, increment: number) {
    const button = new BasicButton({
      up: arrowImage,
      width: this.height * 0.8,
      height: this.height,
    });
    button.onTap = () => {
      this._value = numberClamp(
        this._value - increment,
        0,
        this._colorIds.length - 1,
      );
      this._updateText();
      this.onChanged?.(this.value);
    };
    button.initListeners();
    this.addChild(button);
    button['_update']();
    button.x = 0;
    button.y = 0;
  }

  private _spawnIncrement(options: ColorPickerOptions, increment: number) {
    const button = new BasicButton({
      up: arrowImage,
      width: this.height * 0.8,
      height: this.height,
    });
    button.scaleX = -1;
    button.onTap = () => {
      this._value = numberClamp(
        this._value + increment,
        0,
        this._colorIds.length - 1,
      );
      this._updateText();
      this.onChanged?.(this.value as ColorId);
    };
    button.initListeners();
    this.addChild(button);
    button.x = this.width;
    button.y = 0;
  }

  private _spawnText() {
    // spawn number text
    const text = new BasicText({
      extraStyle: {
        fill: '#fff',
        fontSize: 26,
        // strokeThickness: Math.ceil(options.height * 0.2),
        // lineJoin: 'round',
      },
    });
    text.setTextOpts({
      align: RichTextHorizontalAlign.Center,
      verticalAlign: RichTextVerticalAlign.Center,
      size: 38,
    });
    text.anchorX = 0.5;
    text.anchorY = 0.5;
    text.width = this.width * 0.25;
    text.height = this.height;
    this.addChild(text);

    // add prompt on tap
    text.interactive = true;
    text.setOnTap(() => {
      const result = prompt('COLOR', this.value.toString());
      if (this._colorIds.includes(result as ColorId)) {
        this.value = result as ColorId;
        this.onChanged?.(result as ColorId);
      }
    });
    text.x = this.width / 2;
    text.y = this.height / 2 - 5;

    this._text = text;
  }
}
